'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { startNavigationLoadingForHref } from '@/lib/navigation-loading';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  CalendarDays,
  Plus,
  UserCheck,
  Users,
  Link2,
  BarChart3,
  Calendar,
  DollarSign,
  Bell,
  MessageCircle,
  MessageSquare,
  Building2,
  Settings,
  Code2,
  Shield,
  FileText,
  Palette,
  ClipboardList,
} from 'lucide-react';
import { getCreateEventPath } from '@/lib/create-event';
import { useAuthStore } from '@/lib/auth-store';
import {
  canAccessOrganizationNavItem,
  type OrganizationNavKey,
} from '@/lib/organization-access';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';

type NavItem = {
  key: OrganizationNavKey;
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const organizationNavSections: NavSection[] = [
  {
    title: 'Main',
    items: [{ key: 'dashboard', href: '/dashboard/organization', label: 'Overview', icon: LayoutDashboard }],
  },
  {
    title: 'Events',
    items: [
      { key: 'events', href: '/dashboard/organization/events', label: 'Organization Events', icon: CalendarDays },
      {
        key: 'create_event',
        href: getCreateEventPath({
          isAuthenticated: true,
          canCreateEvents: true,
          workspace: 'organization',
        }),
        label: 'Create Event',
        icon: Plus,
      },
      { key: 'calendar', href: '/dashboard/calendar', label: 'Calendar', icon: Calendar },
      { key: 'analytics', href: '/dashboard/organization/analytics', label: 'Analytics & Reports', icon: BarChart3 },
      { key: 'forms', href: '/dashboard/forms', label: 'Forms', icon: ClipboardList },
    ],
  },
  {
    title: 'Team',
    items: [
      { key: 'organizers', href: '/dashboard/organization/organizers', label: 'Organizer Management', icon: UserCheck },
      { key: 'roles', href: '/dashboard/organization/roles', label: 'Organization Roles', icon: Shield },
      { key: 'organizer_requests', href: '/dashboard/organization/organizer-requests', label: 'Organizer Requests', icon: UserCheck },
      { key: 'assignments', href: '/dashboard/organization/assignments', label: 'Event Assignment', icon: Link2 },
    ],
  },
  {
    title: 'Finance',
    items: [
      { key: 'finance', href: '/dashboard/organization/finance', label: 'Finance', icon: DollarSign },
    ],
  },
  {
    title: 'Account',
    items: [
      { key: 'endorsements', href: '/dashboard/endorsements', label: 'Pending Actions', icon: Bell },
      { key: 'notifications', href: '/dashboard/notifications', label: 'Notifications', icon: Bell },
      { key: 'messages', href: '/dashboard/organization/messages', label: 'Organizer Chat', icon: MessageSquare },
      { key: 'support', href: '/dashboard/support', label: 'Support', icon: MessageCircle },
      { key: 'profile', href: '/dashboard/organization/profile', label: 'Organization Profile', icon: Building2 },
      { key: 'settings', href: '/dashboard/organization/settings', label: 'Settings', icon: Settings },
      { key: 'developer', href: '/dashboard/developer', label: 'Developer', icon: Code2 },
    ],
  },
];

const organizationNav: NavItem[] = organizationNavSections.flatMap((section) => section.items);

/** Longest matching href wins so parent routes (e.g. /edop) don't stay active on child pages. */
function resolveActiveNavHref(pathname: string | null, items: NavItem[]): string | null {
  if (!pathname) return null;

  let best: NavItem | null = null;
  for (const item of items) {
    const exact = pathname === item.href;
    const nested = item.href !== '/dashboard/organization' && pathname.startsWith(`${item.href}/`);
    if (!exact && !nested) continue;
    if (!best || item.href.length > best.href.length) {
      best = item;
    }
  }
  return best?.href ?? null;
}

export function OrganizationSidebar() {
  const pathname = usePathname();
  const { user } = useAuthStore();

  const visibleSections = organizationNavSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canAccessOrganizationNavItem(user, item.key)),
    }))
    .filter((section) => section.items.length > 0);
  const visibleNav = organizationNav.filter((item) => canAccessOrganizationNavItem(user, item.key));
  const activeHref = resolveActiveNavHref(pathname, visibleNav);

  useEffect(() => {
    if (user && visibleSections.length === 0) {
      console.warn('[OrganizationSidebar] No navigation items rendered for user:', {
        userId: user.id,
        role: user.role,
        userType: user.user_type,
        roles: user.roles,
        has_organization_access: user.has_organization_access,
      });
    }
  }, [user, visibleSections.length]);

  return (
    <aside className="eh-sidebar flex md:sticky md:top-0 h-screen sidebar shrink-0 w-full overflow-hidden">
      <Link href="/" className="block px-5 pt-6 pb-4 border-b border-white/5" aria-label="EnthiosHub home">
        <SidebarBrandLogo />
      </Link>

      <div className="eh-sidebar-scroll sidebar-inner custom-scrollbar space-y-5 pb-20 pt-1">
        {visibleSections.length === 0 ? (
          <div className="px-3 py-4 space-y-3">
            <div className="px-1">
              <p className="eh-sidebar-section">Overview</p>
            </div>
            <nav className="space-y-1 px-1">
              <Link
                href="/dashboard/organizer"
                prefetch={false}
                onClick={() => startNavigationLoadingForHref('/dashboard/organizer', pathname)}
                className="eh-sidebar-link group"
              >
                <LayoutDashboard className="eh-sidebar-link-icon group-hover:text-text-inverse" />
                Organizer Dashboard
              </Link>
              <Link
                href="/dashboard/events"
                prefetch={false}
                onClick={() => startNavigationLoadingForHref('/dashboard/events', pathname)}
                className="eh-sidebar-link group"
              >
                <CalendarDays className="eh-sidebar-link-icon group-hover:text-text-inverse" />
                My Events
              </Link>
            </nav>
          </div>
        ) : (
          visibleSections.map((section) => (
            <div key={section.title} className="space-y-1">
              <div className="px-3">
                <p className="eh-sidebar-section">
                  {section.title}
                </p>
              </div>
              <nav className="space-y-1 px-1" aria-label={section.title}>
                {section.items.map((item) => {
                  const isActive = activeHref === item.href;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={`${item.href}-${item.label}`}
                      href={item.href}
                      prefetch={false}
                      onClick={() => startNavigationLoadingForHref(item.href, pathname)}
                      className={`eh-sidebar-link group ${
                        isActive ? 'eh-sidebar-link--pill-active' : ''
                      }`}
                    >
                      <Icon className={`eh-sidebar-link-icon ${isActive ? '' : 'group-hover:text-text-inverse'}`} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
